import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { MedicationStatement } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

// Batch Create Medication Types
export interface CodeableConcept {
  coding: Array<{
    system: string;
    code: string;
    display?: string;
  }>;
  text?: string;
}

export interface MedicationStatementCriteria {
  effectivePeriodStart?: string;
  effectivePeriodEnd?: string;
  note?: string;
  status?:
    | (typeof MedicationStatement.Status.values)[keyof typeof MedicationStatement.Status.values]
    | null;
}

export interface BatchCreateMedicationStatementCriteria {
  medication: CodeableConcept;
  medicationStatement: MedicationStatementCriteria | null;
}

export interface BatchCreateMedicationStatementsCriteriaMetadata {
  btChatExtractionSpanId?: string;
}

export interface BatchCreateMedicationStatementsCriteria {
  subjectId: string;
  encounterId?: string;
  medications?: CodeableConcept[];
  medicationsCriteria?: BatchCreateMedicationStatementCriteria[];
  metadata?: BatchCreateMedicationStatementsCriteriaMetadata;
}

export enum BatchCreateMedicationStatementsFailureReason {
  FAILED_TO_CONSTRUCT = "FAILED_TO_CONSTRUCT",
  FAILED_TO_PERSIST = "FAILED_TO_PERSIST",
}

export enum BatchCreateMedicationStatementsWarningReason {
  FAILED_TO_SYNC = "FAILED_TO_SYNC",
  FAILED_TO_UPDATE_LHR = "FAILED_TO_UPDATE_LHR",
}

export interface BatchCreateMedicationStatementsFailure {
  medication: CodeableConcept;
  failureReason: BatchCreateMedicationStatementsFailureReason;
}

export interface BatchCreateMedicationStatementsWarning {
  medicationStatementId?: string;
  warnings: BatchCreateMedicationStatementsWarningReason[];
}

export interface BatchCreateMedicationStatementsResponse {
  successfulIds: string[];
  warnings: BatchCreateMedicationStatementsWarning[];
  failures: BatchCreateMedicationStatementsFailure[];
  medicationStatementResources: MedicationStatement[];
}

export const useBatchCreateMedications = ({
  patientId,
  onSuccess,
}: {
  patientId: string;
  onSuccess?: (response: BatchCreateMedicationStatementsResponse) => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<
    BatchCreateMedicationStatementsResponse,
    BatchCreateMedicationStatementsCriteria
  >({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Medications.BATCH_CREATE(patientId),
      mutationFn: async (payload: BatchCreateMedicationStatementsCriteria) => {
        const response = await axiosInstance.post(
          "/rx/medications/hx/batch-create",
          payload,
        );
        return response?.data;
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        onSuccess?.(data);
      },
    },
  });
};
